import React, { useCallback } from 'react'
import { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { dispatchAppEvent } from '@/src/renderer/lib/events'
import { SidebarItem } from '../SidebarItem'
import { SidebarSubMenu } from '../SidebarSubMenu'
import { AreaIcon } from '../../../../components/icons/AreaIcon'
import { CircularProgress } from '../../../ui/CircularProgress'
import { useSectionsByProject } from '../../../../lib/db/hooks'
import { useProjectTaskProgress } from '../../../../features/taskManagement/hooks/useProjectTaskProgress'
import { ProjectSidebarItemProps } from './ProjectSidebarItem.types'

export const ProjectSidebarItem: React.FC<ProjectSidebarItemProps> = ({
  project,
  lifeSpace,
  activeTab,
  isExpanded,
  onToggleExpand,
  openPaths,
  previewPaths,
  pinnedPaths,
  onOpenTab,
  onDoubleClick,
  onPromote,
  onCloseTabByPath,
  onUnpinByPath,
  onPinByPath,
  openTab,
  onDelete,
  activeSectionId,
  onSectionClick,
}) => {
  const { t } = useTranslation()
  const path = `/home/${lifeSpace}/project/${project.id}`
  const title = project.name || t('project.defaultName')

  const navigate = useNavigate()
  const sections = useSectionsByProject(project.id)
  const hasSubItems = sections.length > 0
  const isProjectActive = activeTab?.path === path
  const hasActiveSubItem = isProjectActive && !!activeSectionId && sections.some(s => s.id === activeSectionId)

  // Progress donut only for the active project AND when:
  //   - the project has at least four countable tasks (total >= 4), AND
  //   - at least one of them is completed (done > 0)
  // Tiny / untouched projects stay quiet. Hover swaps the donut for the
  // close-tab button (SidebarItem.scss via __trailing).
  const progress = useProjectTaskProgress(isProjectActive ? project.id : undefined)
  const showProgress = isProjectActive && progress.total >= 4 && progress.done > 0
  const trailing = showProgress
    ? (
      <CircularProgress
        value={progress.value}
        ariaLabel={t('project.progressLabel', {
          done: progress.done,
          total: progress.total,
          defaultValue: `${progress.done} of ${progress.total} tasks done`,
        })}
      />
    )
    : undefined

  // When clicking the project while already on it, toggle sub-menu and scroll to top
  const handleProjectClick = useCallback((e: React.MouseEvent) => {
    if (isProjectActive) {
      if (hasSubItems) onToggleExpand()
      // Signal ProjectPage to suppress scroll-spy during the smooth scroll
      dispatchAppEvent('scroll-spy-suppress')
      // Clear section param and scroll to top
      navigate(path, { replace: true })
      const scrollWrap = document.querySelector('.main-layout__scroll-wrap')
      if (scrollWrap) scrollWrap.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    onOpenTab(e, `${lifeSpace}-project-${project.id}`, path, title, AreaIcon as unknown as LucideIcon)
  }, [isProjectActive, hasSubItems, onToggleExpand, navigate, path, onOpenTab, lifeSpace, project.id, title])

  const handleOpenNewTab = useCallback(() => {
    openTab({
      id: `${lifeSpace}-project-${project.id}-${Date.now()}`,
      path,
      title,
      icon: AreaIcon as unknown as LucideIcon,
      lifeSpace,
      isDirty: false,
      isPinned: false,
    })
  }, [openTab, lifeSpace, project.id, path, title])

  return (
    <React.Fragment>
      <SidebarItem
        icon={AreaIcon as unknown as LucideIcon}
        label={title}
        path={path}
        isActive={isProjectActive}
        hasOpenTab={openPaths.has(path)}
        isPreview={previewPaths.has(path)}
        isPinned={pinnedPaths.has(path)}
        hasSubItems={hasSubItems}
        isSubMenuExpanded={isExpanded && hasSubItems}
        hasActiveSubItem={hasActiveSubItem}
        onToggleSubMenu={onToggleExpand}
        onUnpin={onUnpinByPath ? () => onUnpinByPath(path) : undefined}
        onPinTab={onPinByPath ? () => onPinByPath(path) : undefined}
        onCloseTab={onCloseTabByPath ? () => onCloseTabByPath(path) : undefined}
        onOpenNewTab={handleOpenNewTab}
        onClick={handleProjectClick}
        onDoubleClick={() => onDoubleClick(path)}
        onPromote={onPromote}
        onDelete={onDelete}
        trailing={trailing}
      />
      <AnimatePresence initial={false}>
        {hasSubItems && isExpanded && (
          <motion.div
            key="sub-menu"
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <motion.div
              initial={{ y: '-100%' }}
              animate={{ y: 0 }}
              exit={{ y: '-100%' }}
              transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <SidebarSubMenu
                items={sections.map(s => ({ id: s.id, label: s.title }))}
                activeItemId={hasActiveSubItem ? activeSectionId : undefined}
                onItemClick={(sectionId) => onSectionClick(project.id, sectionId)}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </React.Fragment>
  )
}
